import React, { useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import { Box, Button, Divider, IconButton } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { PERMISSION_LIST } from 'common/constants/permissions';
import { useBreakpoint } from 'common/hooks';
import { canAccessAnalytics } from 'common/utils/session';

import IcoContacts from 'resources/icoContacts.svg?react';
import IcoFinancial from 'resources/icoFinancial.svg?react';

import { TOOLBAR_BACKGROUND_COLOR } from './constants';
import useCatalogOptions from './hooks/useCatalogOptions';
import useCreditOptions from './hooks/useCreditOptions';
import Logo from './Logo.jsx';
import MenuOption from './MenuOption';
import MenuWithOptions from './MenuWithOptions';
import OtherOptionsMenu from './OtherOptionsMenu.jsx';
import StyledList from './StyledComponents/StyledList';

const DrawerContent = ({
    container,
    drawerSize,
    handleDrawerToggle,
    match,
    mobileOpen,
    setMobileOpen,
    setSidebarOpen,
    sidebarOpen,
    toggleDrawerSize,
}) => {
    const { t } = useTranslation();
    const breakpoint = useBreakpoint();
    const [isOpenDrawer, setOpenDrawer] = useState(false);
    const catalogOptions = useCatalogOptions();
    const creditOptions = useCreditOptions();

    const commonMenuProps = {
        container,
        drawerSize,
        handleDrawerToggle,
        mobileOpen,
        setMobileOpen,
        setOpenDrawer,
        setSidebarOpen,
        sidebarOpen,
        isOpenDrawer,
    };

    const hideAnalytics = !canAccessAnalytics();

    const isMobile = ['xs', 'sm'].includes(breakpoint);

    return (
        <>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: { md: 'center', xs: 'space-between' },
                }}
            >
                <Box sx={{ flex: { md: 1, xs: 0 }, pb: 2 }}>
                    <Box
                        sx={{
                            height: '19px',
                            mx: '10px',
                            width: '24px',
                            '& img': {
                                height: '100%',
                                objectFit: 'contain',
                                width: '100%',
                            },
                        }}
                    >
                        <IconButton
                            component={RouterLink}
                            to={() => {
                                if (!isMobile) return '/';
                                return false;
                            }}
                            sx={{ p: 0 }}
                            onClick={() => {
                                if (mobileOpen) handleDrawerToggle();
                                setOpenDrawer(false);
                            }}
                        >
                            <Logo field={'logotype_image'} />
                        </IconButton>
                    </Box>
                </Box>

                <IconButton
                    onClick={() => {
                        if (mobileOpen) handleDrawerToggle();
                    }}
                    sx={{ display: { md: 'none' } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider sx={{ display: { md: 'none', sx: 'block' } }} />

            <Button
                sx={{
                    alignSelf: 'end',
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? theme.palette.background.paper
                            : TOOLBAR_BACKGROUND_COLOR,
                    border: '1px solid #3a3e4f',
                    borderRadius: 1,
                    boxShadow: 'none',
                    color: '#fffafa',
                    display: {
                        md: 'flex',
                        xs: 'none',
                    },
                    height: '28px',
                    margin: 0,
                    mb: 1,
                    mt: 1,
                    minWidth: '28px',
                    padding: 0,
                    position: 'relative',
                    right: '-12px',
                    width: '28px',
                }}
                onClick={toggleDrawerSize}
            >
                {drawerSize === 'normal' && !sidebarOpen ? (
                    <ChevronLeftIcon sx={{ fontSize: '16px' }} />
                ) : (
                    <ChevronRightIcon sx={{ fontSize: '16px' }} />
                )}
            </Button>

            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    pt: { sm: 1.5, md: 0 },
                }}
            >
                <StyledList
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        pb: 0,
                    }}
                >
                    <MenuWithOptions
                        {...commonMenuProps}
                        Icon={IcoContacts}
                        iconId="sidebar-credits"
                        options={creditOptions}
                        title={t('Credit', { count: 2 })}
                    />

                    <MenuOption
                        {...{
                            ...commonMenuProps,
                            Icon: HandshakeIcon,
                            requiredPermissions: [
                                PERMISSION_LIST.ALLIANCE_REQUESTS_PERMISSION,
                            ],
                            selected:
                                /^\/installers/i.test(match.path) ||
                                /^\/installer-profile/i.test(match.path),
                            title: t('Partner', { count: 2 }),
                            to: '/installers',
                        }}
                    />

                    <MenuWithOptions
                        {...commonMenuProps}
                        Icon={IcoFinancial}
                        iconId="sidebar-catalogs"
                        options={catalogOptions}
                        title={t('Catalog', { count: 2 })}
                    />

                    {!hideAnalytics && (
                        <MenuOption
                            {...{
                                ...commonMenuProps,
                                Icon: SignalCellularAltIcon,
                                selected: '/analytics' === match.url,
                                title: 'Analytics',
                                to: '/analytics',
                            }}
                        />
                    )}
                    <Divider />
                </StyledList>

                <OtherOptionsMenu {...commonMenuProps} />
            </Box>
        </>
    );
};

DrawerContent.propTypes = {
    container: PropTypes.func,
    drawerSize: PropTypes.string,
    handleDrawerToggle: PropTypes.func,
    match: PropTypes.object,
    mobileOpen: PropTypes.bool,
    setMobileOpen: PropTypes.func,
    setSidebarOpen: PropTypes.func,
    sidebarOpen: PropTypes.bool,
    toggleDrawerSize: PropTypes.func,
};

export default DrawerContent;
